import React, { useEffect, useState } from 'react';
import { Task } from '../../types';
import { createTask, deleteTask, fetchTasks, updateTask } from '../../services/api';

interface Props {
  subjectType: 'leads' | 'deals';
  subjectId: number;
}

const TasksList: React.FC<Props> = ({ subjectType, subjectId }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTasks(subjectType, subjectId).then(setTasks);
  }, [subjectType, subjectId]);

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      const task = await createTask(subjectType, subjectId, {
        title: newTitle.trim(),
        due_date: newDue || undefined,
      });
      setTasks([task, ...tasks]);
      setNewTitle('');
      setNewDue('');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (task: Task) => {
    const updated = await updateTask(subjectType, subjectId, task.id, {
      is_done: !task.is_done,
    });
    setTasks(tasks.map((t) => (t.id === updated.id ? updated : t)));
  };

  const handleDelete = async (taskId: number) => {
    await deleteTask(subjectType, subjectId, taskId);
    setTasks(tasks.filter((t) => t.id !== taskId));
  };

  const isOverdue = (task: Task) =>
    !task.is_done && task.due_date && new Date(task.due_date) < new Date();

  return (
    <div className="tasks-list">
      <div className="add-task-form">
        <input
          className="add-task-input"
          placeholder="New task title…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <input
          type="date"
          className="add-task-date"
          value={newDue}
          onChange={(e) => setNewDue(e.target.value)}
        />
        <button
          className="btn btn-primary"
          onClick={handleAdd}
          disabled={saving || !newTitle.trim()}
        >
          Add
        </button>
      </div>

      {tasks.length === 0 && (
        <p className="detail-info-empty">No tasks yet.</p>
      )}

      {tasks.map((task) => (
        <div className={`task-item${task.is_done ? ' task-done' : ''}`} key={task.id}>
          <input
            type="checkbox"
            className="task-checkbox"
            checked={task.is_done}
            onChange={() => handleToggle(task)}
          />
          <span className={`task-title${task.is_done ? ' task-title-done' : ''}`}>
            {task.title}
          </span>
          {task.due_date && (
            <span className={`task-due${isOverdue(task) ? ' task-due-overdue' : ''}`}>
              {new Date(task.due_date).toLocaleDateString()}
            </span>
          )}
          <button
            className="task-delete"
            onClick={() => handleDelete(task.id)}
            title="Delete task"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

export default TasksList;
